import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/auth';

interface MyClass { class_id: number; class: string; is_form_teacher: boolean; subjects: { id: number; name: string }[] }

/**
 * For a teacher using a school-wide page (score entry, import, CBT, attendance): only the classes they are assigned to,
 * and only their subjects in each class. For everyone else nothing is filtered.
 */
export function useTeacherScope() {
  const isTeacher = useAuth((s) => s.user?.role) === 'teacher';
  const q = useQuery({ queryKey: ['teacher-classes'], enabled: isTeacher, queryFn: () => api.get('/teacher/classes').then((r) => { const d = r.data?.data ?? r.data; return (Array.isArray(d) ? d : d?.data ?? []) as MyClass[]; }) });
  const mine = q.data ?? [];
  const classIds = new Set(mine.map((c) => c.class_id));
  return {
    isTeacher,
    loading: isTeacher && q.isLoading,
    /** keep only my classes */
    classes: <T extends { id: number }>(all: T[]): T[] => (isTeacher ? all.filter((c) => classIds.has(c.id)) : all),
    /** keep only subjects I teach (in the chosen class when given) */
    subjects: <T extends { id: number }>(all: T[], classId?: number | string | null): T[] => {
      if (!isTeacher) return all;
      const pool = mine.filter((c) => !classId || c.class_id === Number(classId));
      const ids = new Set(pool.flatMap((c) => (c.is_form_teacher && !c.subjects.length ? [] : c.subjects.map((s) => s.id))));
      return all.filter((s) => ids.has(s.id));
    },
    /** my classes where I teach this subject (all my classes when no subject is chosen) */
    classesFor: <T extends { id: number }>(all: T[], subjectId?: number | string | null): T[] => {
      if (!isTeacher) return all;
      const ok = new Set(mine.filter((c) => !subjectId || c.subjects.some((s) => s.id === Number(subjectId)) || (c.is_form_teacher && !c.subjects.length)).map((c) => c.class_id));
      return all.filter((c) => ok.has(c.id));
    },
    /** what I teach, one row per class and subject */
    assignments: mine.flatMap((c) => c.subjects.map((s) => ({ class_id: c.class_id, class: c.class, subject_id: s.id, subject: s.name }))),
    formClasses: <T extends { id: number }>(all: T[]): T[] => (isTeacher ? all.filter((c) => mine.some((m) => m.class_id === c.id && m.is_form_teacher)) : all),
  };
}
